import React, { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../sub-comp/Navbar";
import FullScreenImage from "../Home/ShowcaseSection";
import Footer from "../sub-comp/MainFooter";
import { getImageUrl } from "../../utils/getImageUrl";

const CyberSecurityTemplate = ({ program }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Parse JSON fields
  const introParagraphsRaw = program.introParagraphs
    ? typeof program.introParagraphs === 'string'
      ? JSON.parse(program.introParagraphs)
      : program.introParagraphs
    : [];
  const introParagraphs = introParagraphsRaw.length > 0
    ? introParagraphsRaw
    : (program.overviewContent || program.description || program.shortDescription)
      ? [program.overviewContent || program.description || program.shortDescription]
      : [];

  const classroomParagraphs = program.classroomParagraphs
    ? typeof program.classroomParagraphs === 'string'
      ? JSON.parse(program.classroomParagraphs)
      : program.classroomParagraphs
    : [];

  const beyondClassroomParagraphs = program.beyondClassroomParagraphs
    ? typeof program.beyondClassroomParagraphs === 'string'
      ? JSON.parse(program.beyondClassroomParagraphs)
      : program.beyondClassroomParagraphs
    : [];

  const differenceParagraphs = program.differenceParagraphs
    ? typeof program.differenceParagraphs === 'string'
      ? JSON.parse(program.differenceParagraphs)
      : program.differenceParagraphs
    : [];

  // Determine back link and label based on department
  const deptSlug = program.department?.slug;
  const backLink = deptSlug
    ? (deptSlug === 'short-course' ? '/course/short-courses' : `/course/${deptSlug}`)
    : "/course/cyber-security";
  const backLabel = program.department?.name
    ? `Back to ${program.department.name}`
    : "Back to Cyber Security";

  const heroImageUrl = getImageUrl(program.heroImage) || "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=2072&q=80";

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page min-h-screen bg-white text-black overflow-hidden relative">
        {/* Hero Section */}
        <section
          className={`relative h-[70vh] flex items-center justify-center transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <img
            src={heroImageUrl}
            alt={program.heroTitle || "Program"}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative z-10 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              {program.heroTitle || "Cybersecurity Program"}
            </h1>
            <p className="max-w-2xl mx-auto text-lg">
              {program.heroSubtitle || ""}
            </p>
          </div>
        </section>

        {/* Main Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20">
          {/* Breadcrumb */}
          <div className="bg-white">
            <div className="max-w-7xl mx-auto px-4 py-4">
              <Link
                to={backLink}
                className="flex items-center text-custom-pink hover:text-custom-pink/70 font-medium transition-all duration-300 hover:translate-x-1 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                {backLabel}
              </Link>
            </div>
          </div>

          {/* Introduction Paragraphs */}
          {introParagraphs.length > 0 && (
            <div
              className={`mb-16 transition-all duration-1000 delay-700 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-10"
              }`}
            >
              {introParagraphs.map((text, idx) => (
                <p key={idx} className="text-lg leading-relaxed mb-6 text-black">
                  {text}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* In the Classroom Section */}
        {program.classroomImage && (
          <section
            className={`w-full mb-16 transition-all duration-1000 delay-900 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="w-full bg-custom-pink py-12">
              <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
                <img
                  src={getImageUrl(program.classroomImage)}
                  alt={program.classroomTitle || "Classroom"}
                  className="w-full h-full object-cover rounded-2xl"
                />
                <div>
                  <h2 className="text-4xl font-bold text-white mb-6">
                    {program.classroomTitle || "IN THE CLASSROOM"}
                  </h2>
                  {classroomParagraphs.map((text, idx) => (
                    <p key={idx} className="text-white leading-relaxed mb-6">
                      {text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Beyond the Classroom Section */}
        {program.beyondClassroomImage && (
          <section
            className={`w-full mb-16 transition-all duration-1000 delay-1100 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="w-full relative py-12 bg-white">
              <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-4xl font-bold text-custom-pink mb-6">
                    {program.beyondClassroomTitle || "BEYOND THE CLASSROOM"}
                  </h2>
                  {beyondClassroomParagraphs.map((text, idx) => (
                    <p key={idx} className="text-black leading-relaxed mb-6">
                      {text}
                    </p>
                  ))}
                </div>
                <div className="relative">
                  <img
                    src={getImageUrl(program.beyondClassroomImage)}
                    alt={program.beyondClassroomTitle || "Beyond Classroom"}
                    className="w-full h-full object-cover rounded-2xl"
                  />
                  <div className="absolute inset-0 bg-white/60 rounded-2xl"></div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* The Difference Section */}
        {program.differenceImage && (
          <section
            className={`w-full transition-all duration-1000 delay-1300 ${
              isVisible ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="w-full py-12 bg-gray-50">
              <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center">
                <img
                  src={getImageUrl(program.differenceImage)}
                  alt={program.differenceTitle || "The Difference"}
                  className="w-full h-full object-cover rounded-2xl"
                />
                <div>
                  <h2 className="text-4xl font-bold text-custom-pink mb-6">
                    {program.differenceTitle || "THE JRIIT DIFFERENCE"}
                  </h2>
                  {differenceParagraphs.map((text, idx) => (
                    <p key={idx} className="text-black leading-relaxed mb-6">
                      {text}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
      <FullScreenImage />
      <Footer />
    </div>
  );
};

export default CyberSecurityTemplate;

